// Global packages and functions
import { useMemo, useRef, useState } from 'react'

// Types
import type { FaqItem, SanityFaqTaxonomyScheme } from '~/types'

interface Props {
  faqs?: FaqItem[] | null
  categories?: SanityFaqTaxonomyScheme['concepts'] | null
}

// Main export
const useFaqs = ({ faqs, categories }: Props) => {
  // Dangling topic references resolve to null, so drop them
  const topics = useMemo(() => (categories ?? []).filter(Boolean), [categories])

  // Seeded rather than set in an effect, so the first topic is active on the server
  const [activeCategory, setActiveCategory] = useState<string | null>(
    topics[0]?.id ?? null
  )
  const topicsRef = useRef<HTMLDivElement>(null)
  const questionsRef = useRef<HTMLDivElement>(null)

  // Every topic's questions are derived up front so they all ship in the HTML,
  // and switching topic only changes which group is visible
  const groups = useMemo(
    () =>
      topics.map(topic => ({
        ...topic,
        items: (faqs ?? []).filter(faq =>
          faq?.categories?.some(e => e._ref == topic.id)
        ),
      })),
    [faqs, topics]
  )

  // Shows the topic's questions. Where the questions stack below the topics, as
  // on mobile, it also scrolls down to them, so the answer to the tap is in view.
  // Side by side they are already visible, so there is nothing to scroll to
  const selectTopic = (id: string) => {
    setActiveCategory(id)

    const topicsBottom = topicsRef.current?.getBoundingClientRect().bottom ?? 0
    const questionsTop = questionsRef.current?.getBoundingClientRect().top ?? 0

    if (questionsTop >= topicsBottom) {
      questionsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  return {
    topics,
    groups,
    activeCategory,
    topicsRef,
    questionsRef,
    selectTopic,
  }
}

export default useFaqs
